import { useQuery } from 'react-query';

import type {
  AgentGateway,
  PatientChatQuestionRange,
  PatientConversationPage,
  PatientConversationSummary,
} from '@mummssoftware/utils';

import { HISTORY_PAGE_SIZE } from '../constants';
import { maiaQueryKeys } from '../queryClient';
import { errorMessage, unwrap } from '../utils/gatewayError';
import defaults from '../translations/en.json';

type UseConversationHistoryResult = {
  /** null until the first load resolves, so the grid can show its loading overlay. */
  rows: PatientConversationSummary[] | null;
  error: string | null;
};

const byStartedAtDesc = (
  a: PatientConversationSummary,
  b: PatientConversationSummary,
) => (b.startedAt || '').localeCompare(a.startedAt || '') || b.id - a.id;

/**
 * Every user's MAIA conversations about this patient (those with an answered
 * question), newest first. Reloads each time `isShown` turns true (the widget
 * passes "not in a chat"), so it is current after a chat ends in this copy of
 * the widget or the other one; the two copies share that one request
 * (queryClient.ts). In MAIA Chats' patient dialog, `range` keeps it to the
 * list's date filter.
 */
export const useConversationHistory = (
  agentGateway: AgentGateway,
  agency: string,
  patientNumber: string | null | undefined,
  isShown: boolean,
  range?: PatientChatQuestionRange | null,
): UseConversationHistoryResult => {
  const query = useQuery(
    maiaQueryKeys.history(agency, patientNumber || '', range),
    async () => {
      const page = unwrap<PatientConversationPage>(
        await agentGateway.listPatientConversationsForPatient(
          patientNumber as string,
          { size: HISTORY_PAGE_SIZE, range },
        ),
      );
      return [...(page?.content || [])].sort(byStartedAtDesc);
    },
    // stale at once: shown again means loaded again
    { enabled: isShown && Boolean(patientNumber), staleTime: 0 },
  );

  if (!patientNumber) {
    return { rows: [], error: null };
  }
  return {
    // a failed first load shows an empty grid under the error
    rows: query.data ?? (query.error ? [] : null),
    error: query.error
      ? errorMessage(query.error, defaults.loadHistoryError)
      : null,
  };
};
